import React from 'react';
import { dayjs } from 'Utilities/DateTimeUtc';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Menu, MenuItem } from '@mui/material';
import { useHasPermissionsV3 } from 'Utilities';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import { PrimaryButton } from 'v3/Components/Shared/Buttons';
import Tag, { TagColor } from 'v3/Components/Shared/Tag';
import cloudyIcon from 'Assets/Icons/cloudy.svg';
import { ReviewerNote, ReviewerNoteDetail, ReviewerNoteThreadItem } from 'v3/Models/ReviewerNotes';
import './ReviewerProfile.NoteDetail.scss';

export const TYPE_TAG_COLOR: Record<string, TagColor> = {
  Recruitment: 'plum',
  License: 'purple',
  Miscellaneous: 'yellow',
  System: 'grey',
};

export const formatNoteTimestamp = (value: string) => dayjs.utc(value).local().format('MMMM D, YYYY h:mm A');

export const getNoteTagLabel = (note: Pick<ReviewerNote, 'isSystem' | 'type'>) =>
  note.isSystem ? 'System' : note.type?.displayName ?? '';

type Props = {
  note: ReviewerNoteDetail;
  onAddComment?: () => void;
  onEditNote?: () => void;
  onDeleteNote?: () => void;
  onCommentMenuClick?: (commentId: string) => void;
  onCommentDeleteClick?: (commentId: string) => void;
};

const ThreadEntry: React.FC<{
  item: ReviewerNoteThreadItem;
  canEdit?: boolean;
  canDelete?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}> = ({ item, canEdit, canDelete, onEdit, onDelete }) => {
  const showMenu = canEdit || canDelete;

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.();
  };
  const handleDelete = () => {
    handleMenuClose();
    onDelete?.();
  };

  return (
    <div className="note-detail__comment">
      <div className="note-detail__comment-meta">
        <span className="note-detail__comment-meta-text">
          {formatNoteTimestamp(item.dateCreated)} | {item.createdBy}
        </span>
        {showMenu && (
          <>
            <button
              className="note-detail__comment-menu-btn"
              type="button"
              aria-label="Comment actions"
              onClick={handleMenuOpen}
            >
              <MoreVertIcon />
            </button>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              classes={{ paper: 'note-detail__menu-paper' }}
              MenuListProps={{ className: 'note-detail__menu-list' }}
            >
              {canEdit && (
                <MenuItem className="note-detail__menu-item" onClick={handleEdit}>
                  Edit Comment
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem
                  className="note-detail__menu-item note-detail__menu-item--danger"
                  onClick={handleDelete}
                >
                  Delete Comment
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
      <div className="note-detail__comment-text">{item.comment}</div>
    </div>
  );
};

const NoteDetailEmptyState: React.FC<{ canAddComment: boolean; onAddComment?: () => void }> = ({
  canAddComment,
  onAddComment,
}) => (
  <div className="note-detail__empty">
    <img src={cloudyIcon} alt="" aria-hidden="true" className="note-detail__mascot" />
    <div className="note-detail__empty-text">
      <span className="note-detail__empty-heading">No comments yet</span>
      <span className="note-detail__empty-subtext">ADD A COMMENT TO GET STARTED</span>
    </div>
    {canAddComment && (
      <PrimaryButton className="centered" title="Add Comment" onClick={onAddComment} />
    )}
  </div>
);

export const NoteDetailCard: React.FC<Props> = ({
  note,
  onAddComment,
  onEditNote,
  onDeleteNote,
  onCommentMenuClick,
  onCommentDeleteClick,
}) => {
  const tagLabel = getNoteTagLabel(note);
  const tagColor = TYPE_TAG_COLOR[tagLabel] ?? 'grey';

  const hasPermission = useHasPermissionsV3();
  const canAddComment = hasPermission(PermissionCodesV3.Reviewer.Note.Comment);
  // System-generated notes can never be edited or deleted, regardless of permission.
  const canEditNote = hasPermission(PermissionCodesV3.Reviewer.Note.Edit) && !note.isSystem;
  const canDeleteNote = hasPermission(PermissionCodesV3.Reviewer.Note.Delete) && !note.isSystem;
  const canEditBody = hasPermission(PermissionCodesV3.Reviewer.Note.EditBody) && !note.isSystem;
  const canEditComment = hasPermission(PermissionCodesV3.Reviewer.Note.EditComment);
  const canDeleteComment = hasPermission(PermissionCodesV3.Reviewer.Note.DeleteComment);
  const showNoteMenu = canAddComment || canEditNote || canDeleteNote;

  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleAddComment = () => {
    handleMenuClose();
    onAddComment?.();
  };
  const handleEditNote = () => {
    handleMenuClose();
    onEditNote?.();
  };
  const handleDeleteNote = () => {
    handleMenuClose();
    onDeleteNote?.();
  };

  const thread: ReviewerNoteThreadItem[] = [
    { id: note.commentId, comment: note.comment, dateCreated: note.dateCreated, createdBy: note.createdBy },
    ...note.comments,
  ];

  return (
    <div className="note-detail">
      <div className="note-detail__header">
        <h3 className="note-detail__title">{note.title}</h3>
        <div className="note-detail__header-actions">
          <div className="note-detail__comment-count">
            <ChatBubbleOutlineIcon />
            <span>{note.commentCount}</span>
          </div>
          {tagLabel && <Tag label={tagLabel} color={tagColor} />}
          {showNoteMenu && (
            <>
              <button
                className="note-detail__menu-btn"
                type="button"
                aria-label="Note actions"
                onClick={handleMenuOpen}
              >
                <MoreVertIcon />
              </button>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                classes={{ paper: 'note-detail__menu-paper' }}
                MenuListProps={{ className: 'note-detail__menu-list' }}
              >
                {canAddComment && (
                  <MenuItem className="note-detail__menu-item" onClick={handleAddComment}>
                    Add Comment
                  </MenuItem>
                )}
                {canEditNote && (
                  <MenuItem className="note-detail__menu-item" onClick={handleEditNote}>
                    Edit Note
                  </MenuItem>
                )}
                {canDeleteNote && (
                  <MenuItem
                    className="note-detail__menu-item note-detail__menu-item--danger"
                    onClick={handleDeleteNote}
                  >
                    Delete Note
                  </MenuItem>
                )}
              </Menu>
            </>
          )}
        </div>
      </div>
      <div className={`note-detail__body ${note.commentCount === 0 ? 'note-detail__body--empty' : ''}`}>
        {note.commentCount === 0 ? (
          <NoteDetailEmptyState canAddComment={canAddComment} onAddComment={onAddComment} />
        ) : (
          <div className="note-detail__thread">
            {thread.map((item, index) => {
              // The first thread entry is the note body: it can only be edited (via EditBody)
              // and can never be deleted directly, unlike the rest of the thread
              // (EditComment/DeleteComment). For system notes, only the body (the original
              // system-generated entry) is locked down — later comments remain editable.
              const isBody = index === 0;
              return (
                <React.Fragment key={item.id}>
                  {index > 0 && <div className="note-detail__divider" />}
                  <ThreadEntry
                    item={item}
                    canEdit={isBody ? canEditBody : canEditComment}
                    canDelete={isBody ? false : canDeleteComment}
                    onEdit={() => onCommentMenuClick?.(item.id)}
                    onDelete={() => onCommentDeleteClick?.(item.id)}
                  />
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const NoteDetailSkeleton: React.FC = () => (
  <div className="note-detail note-detail--skeleton">
    <div className="note-detail__header">
      <div className="note-detail__skeleton-bone note-detail__skeleton-title" />
      <div className="note-detail__header-actions">
        <div className="note-detail__skeleton-bone note-detail__skeleton-count" />
        <div className="note-detail__skeleton-bone note-detail__skeleton-tag" />
        <div className="note-detail__skeleton-bone note-detail__skeleton-menu" />
      </div>
    </div>
    <div className="note-detail__body">
      <div className="note-detail__thread">
        {[0, 1].map((i) => (
          <React.Fragment key={i}>
            {i > 0 && <div className="note-detail__divider" />}
            <div className="note-detail__comment">
              <div className="note-detail__skeleton-bone note-detail__skeleton-meta" />
              <div className="note-detail__skeleton-bone note-detail__skeleton-line" />
              <div className="note-detail__skeleton-bone note-detail__skeleton-line" />
              <div className="note-detail__skeleton-bone note-detail__skeleton-line--short" />
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
);

export default NoteDetailCard;
